/* eslint-disable react-hooks/exhaustive-deps */
import { AppstoreFilled, EyeFilled, RightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Divider, Table, Tag, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import SearchInput from '../../components/ui/SearchInput';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';
import { getProductActivity } from '../../redux/actions/products';
import { formatCount, getDateRangeFormat } from '../../utils';
import ActivityDetail from './ActivityDetail';
import { useMemo } from 'react';
import Categories from '../../components/ui/Categories';
import { useSelector } from 'react-redux';
import { clearFilters, removeCategory, setCategory } from '../../redux/slices/filtersSlice';
import { store } from '../../redux/store';

const defaultFilters = {
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
    searchText: '',
    sub_category: [],
};

const ProductActivity = () => {
    const { filters, applyFilters } = usePersistentFilters(defaultFilters);
    const { category } = useSelector((s) => s.filtersSlice);

    const [products, setProducts] = useState([]);
    const [activityDetailRecord, setActivityDetailRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [categoryOpen, setCategoryOpen] = useState(false);

    const columns = [
        {
            title: 'Product',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (text, record) => (
                <span>
                    {record?.product_code} - {text}
                </span>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'sub_category',
            key: 'sub_category',
        },
        {
            title: 'OnOrder',
            dataIndex: 'on_order_quantity',
            key: 'on_order_quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'Invoiced',
            dataIndex: 'posted_quantity',
            key: 'posted_quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'OnOrder + Invoiced',
            dataIndex: 'total_quantity',
            key: 'total_quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'Total Collection',
            dataIndex: 'collection_quantity',
            key: 'collection_quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'Total Delivery',
            dataIndex: 'delivery_quantity',
            key: 'delivery_quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'Available',
            dataIndex: 'available_quantity',
            key: 'available_quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'OnHand',
            dataIndex: 'on_hand_quantity',
            key: 'on_hand_quantity',
            align: 'center',
            render: (text, record) => {
                const quantity = record?.available_quantity - record?.on_order_quantity;
                return formatCount(quantity);
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <Button
                    size="small"
                    color="primary"
                    variant="filled"
                    icon={<EyeFilled />}
                    onClick={() => setActivityDetailRecord(record)}
                />
            ),
        },
    ];

    const fetchData = async (params = {}) => {
        setLoading(true);
        const res = await getProductActivity(params);
        setProducts(res?.product || []);
        setLoading(false);
    };

    const tableComponents = useMemo(() => ({
        body: {
            cell: ({ className, ...rest }) => (
                <td {...rest} className={products?.length ? clsx(className, 'py-1') : className} />
            ),
        },
    }), [products?.length]);

    const handleApplyCategory = (categories) => {
        const nextFilters = applyFilters({ sub_category: categories });
        fetchData({
            ...nextFilters,
        });
    };

    const handleRemoveCategory = (filter) => {
        const updated = category.filter((c) => c !== filter);
        store.dispatch(removeCategory(filter));
        const nextFilters = applyFilters({ sub_category: updated });
        fetchData({
            ...nextFilters,
        });
    };


    useEffect(() => {
        store.dispatch(setCategory(filters.sub_category || []));
        fetchData({ ...filters });
    }, []);

    return (
        <div className="product-activity-page">
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className="page-title">
                        Product Activity
                    </Typography.Title>
                </div>

                <div className="flex align-items-center gap-2 flex-wrap">
                    <SearchInput
                        className="lg:max-w-15rem w-full"
                        value={filters.searchText}
                        onSearch={(value) => {
                            const nextFilters = applyFilters({ searchText: value });

                            fetchData({
                                ...nextFilters,
                            });
                        }}
                    />

                    <DatePicker.RangePicker
                        format="DD-MM-YYYY"
                        size="large"
                        className="lg:max-w-20rem w-full"
                        value={[
                            filters.start_date ? dayjs(filters.start_date) : null,
                            filters.end_date ? dayjs(filters.end_date) : null,
                        ]}
                        onChange={(dates) => {
                            const { start_date, end_date } = getDateRangeFormat(dates);
                            const nextFilters = applyFilters({
                                start_date,
                                end_date,
                            });

                            fetchData({
                                ...nextFilters,
                            });
                        }}
                        allowClear={false}
                    />

                    <Button
                        size="large"
                        variant="filled"
                        color="default"
                        icon={<AppstoreFilled />}
                        onClick={() => setCategoryOpen(true)}
                    >
                        Categories {!!category?.length && `(${category.length})`} <RightOutlined />
                    </Button>
                </div>
            </div>
            {category?.length > 0 && (
                <>
                    <div className="flex align-items-center gap-2 overflow-x-auto no-scrollbar py-1">
                        <Typography.Text className="flex-shrink-0">Active Categories:</Typography.Text>
                        <div className="flex gap-2 flex-nowrap">
                            {category.map((filter, i) => (
                                <Tag
                                    color="#f5f5f5"
                                    className="filter-tag shadow-1 whitespace-nowrap"
                                    key={i}
                                    closable
                                    onClose={() => handleRemoveCategory(filter)}
                                >
                                    {filter}
                                </Tag>
                            ))}
                        </div>

                        <Button
                            size="small"
                            color="danger"
                            danger
                            variant="text"
                            onClick={() => {
                                store.dispatch(clearFilters());
                                const nextFilters = applyFilters({ sub_category: [] });
                                fetchData({
                                    ...nextFilters,
                                });
                            }}
                        >
                            Clear All
                        </Button>
                    </div>

                    <Divider className="my-2" />
                </>
            )}


            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.product_netsuite_id}
                dataSource={products}
                pagination={false}
                loading={loading}
                scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                components={tableComponents}
            />

            {categoryOpen && (
                <Categories
                    open={categoryOpen}
                    onClose={() => setCategoryOpen(false)}
                    onApply={handleApplyCategory}
                    value={category}
                />
            )}

            {activityDetailRecord && (
                <ActivityDetail
                    filter={{
                        product_netsuite_id: activityDetailRecord.product_netsuite_id,
                        start_date: filters?.start_date,
                        end_date: filters?.end_date,
                        product_name: activityDetailRecord.product_name,
                        product_code: activityDetailRecord.product_code,
                        available_quantity: activityDetailRecord.available_quantity,
                        on_hand_quantity: (activityDetailRecord?.available_quantity - activityDetailRecord?.on_order_quantity)
                    }}
                    open={!!activityDetailRecord}
                    onClose={() => setActivityDetailRecord(null)}
                />
            )}
        </div>
    );
};

export default ProductActivity;
